import IFirm from "../interfaces/models/IFirm";
import { IOfficeNew } from "../interfaces/models/IOffice";
import Axios from "../utils/axios";

const axios = new Axios();

export const getFirms = async (): Promise<IFirm[]> => {
  try {
    const response = await axios.Get('/firm');
    return response.data;
  } catch (error) {
    console.error('Error fetching firms:', error);
    return [];
  }
}

export const getOfficesByFirmId = async (id:string): Promise<IOfficeNew[]> => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({firm: {value: id, comparisonType: 'EQUAL'}}))
    const response = await axios.Get(`/office?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching firms:', error);
    return [];
  }
}
export const getAllFirms = async (getHidenFirms: boolean = true): Promise<IFirm[]> => {
  try {
    const response = await axios.Get(`/firm?withWSG=true&getHidenFirms=${getHidenFirms.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching firms:', error);
    return [];
  }
}

/**
 * Lightweight firm list (only id/name/firmName) for screens that just resolve names,
 * e.g. the staff Events list Host/Sponsor column. Avoids transferring full firm docs
 * (logos, overview, addresses, notable images) for the whole catalog.
 */
export type FirmNameLookup = Pick<
  IFirm,
  "id" | "name" | "firmName" | "shortName" | "regionNames"
>;

export const getAllFirmNames = async (
  getHidenFirms: boolean = true
): Promise<FirmNameLookup[]> => {
  try {
    const params = new URLSearchParams();
    params.append('withWSG', 'true');
    params.append('getHidenFirms', getHidenFirms.toString());
    params.append(
      'fields',
      JSON.stringify(['id', 'name', 'firmName', 'shortName', 'regionNames'])
    );
    const response = await axios.Get(`/firm?${params.toString()}`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching firm names:', error);
    return [];
  }
}

export const getWSGFirmIds = async (): Promise<string[]> => {
  try {
    const firms = await getAllFirms();
    const wsgKeywords = ['wsg', 'world services group'];
    return firms
      .filter((f: any) => {
        const searchText = ((f?.name || f?.firmName || f?.shortName || '') as string).toLowerCase();
        return wsgKeywords.some((kw) => searchText.includes(kw));
      })
      .map((f: any) => f.id)
      .filter((id: string | undefined): id is string => !!id);
  } catch (error) {
    console.error('Error fetching WSG firm IDs:', error);
    return [];
  }
};

export const getFirmById = async (id: string): Promise<IFirm> => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({id: {value: id, comparisonType: 'EQUAL'}}))
    params.append('withWSG', 'true');
    const response = await axios.Get(`/firm?${params.toString()}`);
    return response.data[0];
  } catch (error) {
    console.error('Error fetching firm:', error);
    throw error;
  }
}

export const getFirmByShortId = async (shortId: string): Promise<IFirm | undefined> => {
  try {
    const trimmedShortId = shortId.trim();
    if (!trimmedShortId) {
      return undefined;
    }

    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({shortId: {value: trimmedShortId, comparisonType: 'EQUAL'}}))
    params.append('withWSG', 'true');
    const response = await axios.Get(`/firm?${params.toString()}`);
    if (response.data[0]) {
      return response.data[0];
    }

    const numericShortId = Number(trimmedShortId);
    if (Number.isNaN(numericShortId)) {
      return undefined;
    }

    const numericParams = new URLSearchParams();
    numericParams.append('filter', JSON.stringify({shortId: {value: numericShortId, comparisonType: 'EQUAL'}}))
    numericParams.append('withWSG', 'true');
    const numericResponse = await axios.Get(`/firm?${numericParams.toString()}`);
    return numericResponse.data[0];
  } catch (error) {
    console.error('Error fetching firm by shortId:', error);
    return undefined;
  }
}

export const getFirmByLegacyId = async (id: number): Promise<IFirm> => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({legacyId: {value: id, comparisonType: 'EQUAL'}}))
    params.append('withWSG', 'true');
    const response = await axios.Get(`/firm?${params.toString()}`);
    return response.data[0];
  } catch (error) {
    console.error('Error fetching firm:', error);
    throw error;
  }
}


const isUuidFormat = (id: string | number): boolean => {
  if (typeof id === 'number') {
    return false;
  }
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

export const getFirmByIdOrLegacyId = async (id: string | number): Promise<IFirm | undefined> => {
  try {
    const idString = String(id);
    
    if (isUuidFormat(idString)) {
      return await getFirmById(idString);
    } else {
      const numericId = typeof id === 'number' ? id : parseInt(idString, 10);
      if (isNaN(numericId)) {
        console.warn(`Invalid firm ID format: ${id}`);
        return undefined;
      }
      return await getFirmByLegacyId(numericId);
    }
  } catch (error) {
    console.error(`Error fetching firm with ID ${id}:`, error);
    return undefined;
  }
};

export const createFirm = async (firm: IFirm): Promise<IFirm> => {
  try {
    const response = await axios.Post('/firm', firm);
    return response.data;
  } catch (error) {
    console.error('Error creating firm:', error);
    throw error;
  }
}

export const updateFirm = async (firm: IFirm): Promise<IFirm> => {
  try {
    const response = await axios.Put(`/firm`, firm);
    return response.data;
  } catch (error) {
    console.error('Error updating firm:', error);
    throw error;
  }
}

export type ChangeFirmMembershipStatusPayload = {
  firmId: string;
  membershipStatus: string;
  membershipType?: string;
  changedDate?: string;
};

export type ChangeFirmMembershipStatusResponse = {
  firm: IFirm;
  officesUpdated: number;
  groupCreated: boolean;
};

export const changeFirmMembershipStatus = async (
  payload: ChangeFirmMembershipStatusPayload,
): Promise<ChangeFirmMembershipStatusResponse> => {
  try {
    const response = await axios.Post(
      "/firm?action=changeMembershipStatus",
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error changing firm membership status:", error);
    throw error;
  }
}

export const deleteFirm = async (id: string): Promise<boolean> => {
  try {
    const response = await axios.Delete(`/firm`, `${id}`);
    return response.status === 200;
  } catch (error) {
    console.error('Error deleting firm:', error);
    return false;
  }
}

export const getLogoByIds = async (id: string[]): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    params.append('fields', JSON.stringify(['id','logo']));
    params.append('filter', JSON.stringify({id: {value: id, comparisonType: 'EQUAL'}}))
    const response = await axios.Get(`/firm?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching firm:', error);
    throw error;
  }
}
